import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiTrendingUp, 
  FiHome, FiDollarSign, FiPieChart, FiActivity, 
  FiAlertTriangle, FiCheck, FiX, FiInfo, FiSearch 
} = FiIcons;

function RentVsBuy() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Mieten oder Kaufen in Deutschland heute: Was Daten und Trends wirklich nahelegen",
    "alternativeHeadline": "Immobilienkauf vs. Mieten & ETF: Ein rationaler Vergleich 2026",
    "image": "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    "author": {
      "@type": "Person",
      "name": "Maxim Kelm"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MK-AUSBAU",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ausbau.nrw/logo-icon.svg"
      }
    },
    "datePublished": "2026-03-28",
    "description": "Lohnt sich der Immobilienkauf 2026 noch? Ein datenbasierter Vergleich zwischen Eigenheim und Mieten + ETF-Sparplan unter Berücksichtigung der Zinswende."
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Mieten oder Kaufen 2026? Daten, Trends & Entscheidungshilfe"
        description="Eigenheim oder Miete? 🏠 Rechnen Sie nach: Zinsen, Kaufpreise vs. ETF-Rendite. Ein ehrlicher Ratgeber für rationale Investoren."
        keywords="Mieten oder Kaufen Rechner, Immobilienblase 2026, Kaufen vs Mieten ETF, Bauzinsen Entwicklung, Immobilienmarkt Deutschland Trends"
        schema={articleSchema}
        canonical="/blog/mieten-oder-kaufen"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link to="/blog" className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group">
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiPieChart} />
              <span>Finanzen & Marktanalysen</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Mieten oder Kaufen in Deutschland heute: Was Daten und Trends wirklich nahelegen
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Finanz-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Stand: März 2026</span>
            </div>
          </header>

          {/* Hero Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl aspect-video relative">
            <img 
              src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80" 
              alt="Taschenrechner und Hausmodell auf Bauplänen - Symbolbild für Mieten oder Kaufen"
              className="absolute top-0 left-0 w-full h-full object-cover"
            />
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            Ein Eigenheim zu besitzen gilt oft als das ultimative Lebensziel – doch es ist keineswegs automatisch die beste finanzielle Entscheidung. Wohneigentum ist immer auch eine Frage des Lebensstils, gleichzeitig aber eine Investition, die einer nüchternen Betrachtung standhalten sollte.
          </div>

          <p className="text-gray-700 mb-8">
            Dieser Artikel beleuchtet die Entscheidung zwischen Mieten und Kaufen anhand langfristiger Entwicklungen in Deutschland. Wir zeigen, wie sich das Umfeld seit dem Zinsanstieg ab 2022 verändert hat und welche strukturellen Kräfte – Demografie, Regulierung und Urbanisierung – künftig auf die Preise wirken.
          </p>

          <div className="bg-mk-teal-50 p-6 rounded-xl border border-mk-teal-100 mb-12">
            <h3 className="text-mk-teal-900 font-bold mt-0 mb-4 flex items-center">
              <SafeIcon icon={FiSearch} className="mr-2"/> Ziel dieses Beitrags
            </h3>
            <p className="m-0 text-sm text-gray-700">
              Sie sollen nach dem Lesen besser einschätzen können:
            </p>
            <ul className="mt-2 mb-0 space-y-1 text-sm list-disc pl-5 text-gray-700">
              <li>Wann <strong>Mieten & Investieren (ETF)</strong> finanziell überlegen sein kann.</li>
              <li>Wann sich ein <strong>Immobilienkauf</strong> wirklich rechnet.</li>
              <li>Wie Zinsen und Sanierungskosten die Gleichung verändern.</li>
            </ul>
          </div>

          {/* Abschnitt 1: Lebensstil vs. Rechenmodell */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            1. Lebensstil trifft Rechenmodell
          </h2>
          <p className="text-gray-700 mb-6">
            Die Entscheidung für Eigentum ist oft emotional geprägt. Stabilität, Gestaltungsfreiheit und das Gefühl von "Mein Haus, meine Regeln" spielen eine große Rolle. Diese Motive sind legitim – aber sie sind <strong>Konsum, keine Investition</strong>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white border-l-4 border-mk-red-500 p-6 shadow-sm rounded-r-xl">
              <h4 className="font-bold text-gray-900 mb-2">Der historische Daten-Check</h4>
              <p className="text-sm text-gray-600 m-0">
                Langfristige Vergleiche (z.B. Gerd Kommer) zeigen: Wer mietet und das gesparte Eigenkapital sowie die Differenz zur Kreditrate breit gestreut am Aktienmarkt anlegt, konnte über 25-30 Jahre oft <strong>mehr Vermögen aufbauen</strong> als Eigenheimbesitzer.
              </p>
            </div>
            <div className="bg-white border-l-4 border-mk-teal-500 p-6 shadow-sm rounded-r-xl">
              <h4 className="font-bold text-gray-900 mb-2">Die Ausnahme-Phase</h4>
              <p className="text-sm text-gray-600 m-0">
                Die Jahre 2010 bis 2021 waren mit extrem fallenden Zinsen und explodierenden Preisen eine historische <strong>Sonderphase</strong>. Wer hier kaufte, profitierte massiv. Diese Phase ist jedoch vorbei und lässt sich nicht einfach in die Zukunft fortschreiben.
              </p>
            </div>
          </div>

          {/* Abschnitt 2: Zinswende */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="mr-3 text-mk-teal-600" />
            2. Die Zinswende verändert die Spielregeln
          </h2>
          <p className="text-gray-700 mb-6">
            Der Immobilienmarkt hängt am Tropf des Fremdkapitals. Der Anstieg der Bauzinsen von ca. 1% auf zwischenzeitlich über 4% hat die monatlichen Belastungen für Neukäufer oft verdoppelt bis verdreifacht.
          </p>

          <div className="bg-gray-50 p-8 rounded-2xl mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Warum Immobilien sensibler sind als Aktien</h3>
            <ul className="space-y-4">
              <li className="flex items-start">
                <SafeIcon icon={FiAlertTriangle} className="text-orange-500 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <strong>Hebelwirkung (Leverage):</strong>
                  <p className="text-sm text-gray-600 mt-1">Hohe Schulden verstärken Gewinne, aber auch Risiken. Ein Wertverlust von 10% bei 90% Finanzierung vernichtet rechnerisch das gesamte Eigenkapital.</p>
                </div>
              </li>
              <li className="flex items-start">
                <SafeIcon icon={FiHome} className="text-mk-teal-500 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <strong>Klumpenrisiko:</strong>
                  <p className="text-sm text-gray-600 mt-1">Als Eigentümer hängen Sie an einem Objekt, einem Standort, einer Heizung. Mieter sind flexibel.</p>
                </div>
              </li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">Großstadt vs. Umland: Der Mietmultiplikator</h3>
          <p className="text-gray-700 mb-6">
            In Städten wie München oder Hamburg sind die Kaufpreise oft so hoch, dass die Miete im Verhältnis dazu "günstig" ist. Wenn Sie für eine Wohnung 35 Jahreskaltmieten zahlen müssen (Kaufpreis / Jahresmiete &gt; 35), ist Kaufen rein finanziell oft unattraktiv. Im ländlichen Raum kann das Verhältnis bei 20 liegen – hier lohnt sich Kaufen oft eher.
          </p>

          {/* Abschnitt 3: Zukünftige Faktoren */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            3. Zukünftige Einflussfaktoren auf Preise
          </h2>
          <p className="text-gray-700 mb-6">
            Demografische Entwicklungen, strengere Regulierung und steigende Modernisierungsanforderungen könnten den Preisdruck dämpfen, während gleichzeitig die Kosten für Eigentümer strukturell steigen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="text-3xl mb-2">👵</div>
              <h4 className="font-bold text-gray-900 mb-2">Demografie</h4>
              <p className="text-sm text-gray-600">
                Die Boomer-Generation vererbt oder verkauft in den kommenden Jahren. Das Angebot an Bestandsimmobilien wird zunehmen – besonders in strukturschwachen Regionen.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="text-3xl mb-2">🏙️</div>
              <h4 className="font-bold text-gray-900 mb-2">Urbanisierung</h4>
              <p className="text-sm text-gray-600">
                Städte bleiben attraktiv, aber Home-Office macht den "Speckgürtel" weiter. Der Druck auf die absoluten Zentren könnte sich leicht entspannen.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <div className="text-3xl mb-2">🔨</div>
              <h4 className="font-bold text-gray-900 mb-2">Regulierung & Sanierung</h4>
              <p className="text-sm text-gray-600">
                Heizungsgesetz (GEG) und EU-Vorgaben machen Eigentum teurer. Die "zweite Miete" (Instandhaltung) wird oft unterschätzt.
              </p>
            </div>
          </div>

          {/* Fazit */}
          <div className="bg-mk-teal-600 text-white p-10 rounded-[2.5rem] shadow-2xl mt-16 mb-12">
            <h2 className="text-white text-3xl font-bold mb-6 mt-0">Fazit: Rechnen statt Raten</h2>
            <p className="text-mk-teal-50 mb-6">
              Eigentum ist immer beides: Lebensstilentscheidung und Investition. Historisch war Mieten kombiniert mit langfristigem Aktieninvestment häufig finanziell überlegen. Heute sprechen höhere Zinsen und hohe Einstiegspreise eher für Vorsicht beim Kauf und für eine genaue Prüfung der Bausubstanz.
            </p>
            <div className="border-t border-mk-teal-400 pt-6">
              <h4 className="font-bold text-white mb-4">Handlungsempfehlung:</h4>
              <ul className="space-y-3 list-none pl-0 text-white/90">
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="mr-2"/> <strong>Rechnen Sie ehrlich:</strong> Vergleichen Sie Kaltmiete + ETF-Sparrate vs. Kreditrate + Hausgeld + Instandhaltung.</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="mr-2"/> <strong>Nebenkosten beachten:</strong> Kaufnebenkosten (Grunderwerbsteuer, Notar, Makler) sind "verlorenes" Geld beim Start.</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="mr-2"/> <strong>Sanierungspuffer:</strong> Wenn Sie kaufen, kalkulieren Sie 20-30% Puffer für energetische Maßnahmen ein.</li>
              </ul>
            </div>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Sie haben sich für den Kauf entschieden?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Dann kaufen Sie nicht die Katze im Sack. Wir unterstützen Sie bei der technischen Prüfung und der Kostenschätzung für notwendige Modernisierungen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/blog/baukosten-abschaetzen" 
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Baukosten abschätzen
              </Link>
              <Link 
                to="/blog/ankaufspruefung-mehrfamilienhaus" 
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Zur Ankaufsprüfung
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
      
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default RentVsBuy;